import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { LanguageSwitcher } from "../../../components/common/LanguageSwitcher/index.js";
import { AuthEntryVisual } from "./AuthEntryVisual.js";
import { authEntryPanelMotion } from "./auth-entry-motion.js";

interface AuthEntryShellProps {
  children: ReactNode;
  showLanguageSwitcher?: boolean;
  footer?: ReactNode;
  /**
   * Desktop layout locked to the viewport height. The panel becomes a flex
   * column so a child with `min-h-0` and its own overflow scrolls instead of
   * the page; the panel itself scrolls only if nothing can shrink further.
   */
  fitViewport?: boolean;
}

export function AuthEntryShell({
  children,
  showLanguageSwitcher = true,
  footer,
  fitViewport = false,
}: AuthEntryShellProps): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const panelMotion = authEntryPanelMotion(reduceMotion);

  const heroTitle = t("auth.brandTitle");
  const heroDescription = t("auth.brandDescription");
  const logoLabel = t("auth.wordmarkLabel");

  return (
    <main
      className={cn(
        "font-feedbot grid min-h-dvh min-w-0 grid-cols-1 bg-[#f8faf9] text-sm leading-normal text-[#17302e] md:grid-cols-2",
        fitViewport && "fit-desktop:h-dvh fit-desktop:grid-rows-[minmax(0,1fr)]",
      )}
    >
      <div className={cn("hidden min-w-0 md:block", fitViewport && "fit-desktop:min-h-0")}>
        <AuthEntryVisual
          title={heroTitle}
          description={heroDescription}
          logoLabel={logoLabel}
          fitViewport={fitViewport}
        />
      </div>

      <div
        className={cn(
          "flex min-w-0 flex-col",
          fitViewport && "fit-desktop:min-h-0 fit-desktop:overflow-y-auto",
        )}
      >
        <div className="md:hidden">
          <AuthEntryVisual
            title={heroTitle}
            description={heroDescription}
            logoLabel={logoLabel}
            compact
          />
        </div>

        <div
          className={cn(
            "flex flex-1 items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] pt-7 sm:px-6 md:px-10 md:py-10",
            fitViewport && "fit-desktop:min-h-0 short-desktop:py-6",
          )}
        >
          <motion.div
            className={cn(
              "grid w-full min-w-0 max-w-[395px] gap-5",
              fitViewport &&
                "fit-desktop:flex fit-desktop:max-h-full fit-desktop:flex-col short-desktop:gap-4",
            )}
            initial="initial"
            animate="animate"
            variants={panelMotion.variants}
            transition={panelMotion.transition}
          >
            {children}

            {footer}

            {showLanguageSwitcher ? (
              <div
                className={cn(
                  "flex min-w-0 justify-center border-t border-[#d5e5dc] pt-5",
                  fitViewport && "short-desktop:pt-4",
                )}
              >
                <LanguageSwitcher variant="entry" />
              </div>
            ) : null}
          </motion.div>
        </div>
      </div>
    </main>
  );
}
